import { useRef, useState, type FormEvent } from 'react';
import { Badge, Button, EmptyState, ErrorState, Input, Spinner } from '@helios/ui';
import { useDevelopmentChecklist } from '@/hooks/useDevelopmentChecklist';
import {
  developmentStatuses,
  type ChecklistImportPreview,
  type DevelopmentItem,
  type DevelopmentStatus,
} from '@/services/developmentChecklistClient';
import {
  SettingsField,
  SettingsSection,
  SettingsTableScroll,
  settingsControlClass,
} from '@/features/settings/settingsChrome';

const labels: Record<DevelopmentStatus, string> = {
  TODO: 'Outstanding',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  READY_FOR_REVIEW: 'Ready for review',
  ACCEPTED: 'Accepted by reviewer',
};
type Tracker = ReturnType<typeof useDevelopmentChecklist>;
type Draft = {
  itemId: string;
  expectedVersion: number;
  status: DevelopmentStatus;
  owner: string;
  dueDate: string;
  notes: string;
  evidenceReference: string;
};

export function DevelopmentChecklistPanel() {
  const tracker = useDevelopmentChecklist();
  return <ChecklistContent key={tracker.scope} tracker={tracker} />;
}

function ChecklistContent({ tracker }: { tracker: Tracker }) {
  const uploadRevision = useRef(0);
  const [upload, setUpload] = useState<{ fileName: string; content: string } | null>(null);
  const [preview, setPreview] = useState<ChecklistImportPreview | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const data = tracker.query.data;
  const busy =
    tracker.preview.isPending || tracker.importItems.isPending || tracker.update.isPending;
  const readUpload = async (file: File | undefined) => {
    const revision = ++uploadRevision.current;
    setUpload(null);
    setPreview(null);
    setNotice(null);
    setFileError(null);
    tracker.preview.reset();
    tracker.importItems.reset();
    if (!file) return;
    if (file.size > 500_000 || !/\.(csv|tsv|txt)$/i.test(file.name)) {
      setFileError('Choose a CSV, TSV, or plain-text list under 500 KB.');
      return;
    }
    const origin = tracker.identity;
    try {
      const content = await file.text();
      if (tracker.isCurrent(origin) && revision === uploadRevision.current)
        setUpload({ fileName: file.name, content });
    } catch {
      if (tracker.isCurrent(origin) && revision === uploadRevision.current)
        setFileError('This file could not be read.');
    }
  };
  const inspectUpload = async () => {
    if (!upload) return;
    const origin = tracker.identity;
    const revision = uploadRevision.current;
    setPreview(null);
    try {
      const result = await tracker.preview.mutateAsync(upload);
      if (tracker.isCurrent(origin) && revision === uploadRevision.current) setPreview(result);
    } catch {
      /* The API error is shown below. */
    }
  };
  const importUpload = async () => {
    if (!upload || !preview || !data) return;
    const origin = tracker.identity;
    try {
      const saved = await tracker.importItems.mutateAsync({
        ...upload,
        expectedVersion: data.version,
      });
      if (tracker.isCurrent(origin)) {
        setNotice(
          `${preview.items.length} items imported. ${saved.summary.total} total items in this workspace.`,
        );
        setPreview(null);
        setUpload(null);
        tracker.importItems.reset();
        tracker.preview.reset();
      }
    } catch {
      /* Existing progress remains untouched on conflict. */
    }
  };
  const edit = (item: DevelopmentItem) => {
    if (!data) return;
    tracker.update.reset();
    setNotice(null);
    setDraft({
      itemId: item.id,
      expectedVersion: data.version,
      status: item.status,
      owner: item.owner,
      dueDate: item.dueDate ?? '',
      notes: item.notes,
      evidenceReference: item.evidenceReference,
    });
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    const origin = tracker.identity;
    const { itemId, dueDate, ...patch } = draft;
    try {
      await tracker.update.mutateAsync({ itemId, patch: { ...patch, dueDate: dueDate || null } });
      if (tracker.isCurrent(origin)) {
        setDraft(null);
        setNotice('Checklist progress saved.');
      }
    } catch {
      /* Preserve the captured version and edits for review. */
    }
  };
  const error =
    fileError ??
    tracker.preview.error?.message ??
    tracker.importItems.error?.message ??
    tracker.update.error?.message;
  const items =
    data?.items.filter(
      (item) =>
        (!statusFilter || item.status === statusFilter) &&
        `${item.title} ${item.reference} ${item.module} ${item.owner}`
          .toLowerCase()
          .includes(filter.toLowerCase()),
    ) ?? [];
  return (
    <SettingsSection
      title="Development checklist"
      description="Import your master list to track what is outstanding, in progress, due, or awaiting review. Acceptance is a recorded human decision; it does not prove a production deployment."
      actions={
        <Button
          size="xs"
          variant="outline"
          disabled={busy || Boolean(draft)}
          onClick={() => void tracker.query.refetch()}
        >
          Refresh checklist
        </Button>
      }
    >
      {tracker.query.isLoading ? (
        <div className="flex items-center gap-2 text-xs">
          <Spinner className="h-4 w-4" />
          Loading development checklist…
        </div>
      ) : tracker.query.isError ? (
        <ErrorState
          title="Development checklist unavailable"
          description={
            tracker.query.error instanceof Error
              ? tracker.query.error.message
              : 'Checklist could not be loaded.'
          }
          action={
            <Button size="xs" variant="outline" onClick={() => void tracker.query.refetch()}>
              Retry checklist
            </Button>
          }
        />
      ) : data ? (
        <div className="space-y-3">
          {notice ? (
            <p className="text-success text-xs" role="status">
              {notice}
            </p>
          ) : null}
          {error ? (
            <p className="text-critical text-xs" role="alert">
              {error}
            </p>
          ) : null}
          <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
            <SettingsField label="Upload master checklist">
              <Input
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                disabled={busy || Boolean(draft)}
                onChange={(event) => void readUpload(event.target.files?.[0])}
              />
            </SettingsField>
            <Button
              size="xs"
              variant="outline"
              disabled={!upload || busy || Boolean(draft)}
              onClick={() => void inspectUpload()}
            >
              Preview import
            </Button>
          </div>
          <p className="text-2xs text-muted-foreground">
            CSV/TSV: title is required. Optional columns: reference, module, description, owner,
            due_date (YYYY-MM-DD), status, notes, evidence. Plain text: one item per line. Imports
            add items and preserve existing progress.
          </p>
          {preview ? (
            <div
              className="border-border space-y-2 rounded-lg border p-3"
              aria-label="Checklist import preview"
            >
              <p className="text-xs font-medium">
                {preview.fileName} · {preview.items.length} items ready to import
              </p>
              {preview.warnings.map((warning) => (
                <p key={warning} className="text-2xs text-warning">
                  {warning}
                </p>
              ))}
              <ul className="space-y-1 text-xs">
                {preview.items.slice(0, 5).map((item) => (
                  <li key={item.sourceRow}>
                    {item.title} · {labels[item.status]}
                  </li>
                ))}
              </ul>
              {preview.items.length > 5 ? (
                <p className="text-2xs text-muted-foreground">
                  Showing the first 5 items. All {preview.items.length} validated items will be
                  imported.
                </p>
              ) : null}
              <Button size="xs" disabled={busy} onClick={() => void importUpload()}>
                Import {preview.items.length} items
              </Button>
            </div>
          ) : null}
          {data.items.length === 0 ? (
            <EmptyState
              title="Master checklist not uploaded yet"
              description="The tracker starts empty. Upload your approved master list when it is ready; repository sprint records below remain a separate source."
            />
          ) : (
            <>
              <div
                className="grid gap-2 text-xs sm:grid-cols-4"
                aria-label="Development checklist summary"
              >
                <Summary
                  label="Accepted by reviewer"
                  value={`${data.summary.accepted} / ${data.summary.total}`}
                />
                <Summary label="Outstanding" value={String(data.summary.outstanding)} />
                <Summary label="Ready for review" value={String(data.summary.readyForReview)} />
                <Summary label="Overdue" value={String(data.summary.overdue)} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <SettingsField label="Find checklist items">
                  <Input
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                    placeholder="Title, reference, module, owner"
                  />
                </SettingsField>
                <SettingsField label="Checklist status">
                  <select
                    className={settingsControlClass}
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <option value="">All statuses</option>
                    {developmentStatuses.map((status) => (
                      <option key={status} value={status}>
                        {labels[status]}
                      </option>
                    ))}
                  </select>
                </SettingsField>
              </div>
              <SettingsTableScroll label="Development checklist items">
                <table className="w-full min-w-[820px] text-left text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      {['Item', 'Module / owner', 'Status', 'Due', 'Source', 'Progress'].map(
                        (heading) => (
                          <th key={heading} className="border-border border-b px-2 py-2">
                            {heading}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className="border-border border-b">
                        <td className="max-w-[320px] px-2 py-2">
                          <p className="font-medium">{item.title}</p>
                          {item.reference ? (
                            <p className="text-muted-foreground text-2xs">{item.reference}</p>
                          ) : null}
                          {item.description ? (
                            <p className="text-muted-foreground text-2xs">{item.description}</p>
                          ) : null}
                          {item.evidenceReference ? (
                            <p className="text-muted-foreground text-2xs break-all">
                              Evidence: {item.evidenceReference}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-2 py-2">
                          {item.module || 'Unassigned module'}
                          <p className="text-muted-foreground text-2xs">
                            {item.owner || 'No owner assigned'}
                          </p>
                        </td>
                        <td className="px-2 py-2">
                          <Badge
                            variant={
                              item.status === 'ACCEPTED'
                                ? 'success'
                                : item.status === 'BLOCKED'
                                  ? 'critical'
                                  : 'outline'
                            }
                          >
                            {labels[item.status]}
                          </Badge>
                        </td>
                        <td className="px-2 py-2">{item.dueDate ?? 'No due date'}</td>
                        <td className="text-2xs px-2 py-2">
                          {data.sources.find((source) => source.id === item.sourceId)?.fileName ??
                            'Unknown source'}
                          <p>Record {item.sourceRow}</p>
                        </td>
                        <td className="px-2 py-2">
                          <Button
                            size="xs"
                            variant="outline"
                            disabled={busy}
                            onClick={() => edit(item)}
                            aria-label={`Update ${item.title}`}
                          >
                            Update
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SettingsTableScroll>
              {items.length === 0 ? (
                <p className="text-muted-foreground text-xs">No items match these filters.</p>
              ) : null}
            </>
          )}
          {draft ? (
            <form
              onSubmit={(event) => void save(event)}
              className="border-border space-y-3 rounded-lg border p-3"
              aria-label="Update checklist progress"
            >
              <p className="text-xs font-medium">
                Update: {data.items.find((item) => item.id === draft.itemId)?.title}
              </p>
              {draft.expectedVersion !== data.version ? (
                <p className="text-warning text-xs" role="status">
                  The checklist changed while editing. Cancel and reopen this item to review its
                  current progress.
                </p>
              ) : null}
              <div className="grid gap-3 sm:grid-cols-3">
                <SettingsField label="Progress status">
                  <select
                    className={settingsControlClass}
                    value={draft.status}
                    onChange={(event) =>
                      setDraft({ ...draft, status: event.target.value as DevelopmentStatus })
                    }
                  >
                    {developmentStatuses.map((status) => (
                      <option key={status} value={status}>
                        {labels[status]}
                      </option>
                    ))}
                  </select>
                </SettingsField>
                <SettingsField label="Owner">
                  <Input
                    value={draft.owner}
                    maxLength={200}
                    onChange={(event) => setDraft({ ...draft, owner: event.target.value })}
                  />
                </SettingsField>
                <SettingsField label="Due date">
                  <Input
                    type="date"
                    value={draft.dueDate}
                    onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })}
                  />
                </SettingsField>
              </div>
              <SettingsField label="Evidence reference">
                <Input
                  value={draft.evidenceReference}
                  required={draft.status === 'ACCEPTED'}
                  maxLength={2000}
                  placeholder="Release, test record, document, or review reference"
                  onChange={(event) =>
                    setDraft({ ...draft, evidenceReference: event.target.value })
                  }
                />
              </SettingsField>
              <SettingsField label="Progress notes">
                <textarea
                  className={`${settingsControlClass} min-h-20`}
                  value={draft.notes}
                  maxLength={4000}
                  onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                />
              </SettingsField>
              <p className="text-2xs text-muted-foreground">
                Acceptance records your review, the evidence reference, and timestamp. Attachments
                are references here; this tracker does not upload or independently validate their
                contents.
              </p>
              <div className="flex gap-2">
                <Button
                  size="xs"
                  type="submit"
                  disabled={busy || draft.expectedVersion !== data.version}
                >
                  Save progress
                </Button>
                <Button
                  size="xs"
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    setDraft(null);
                    tracker.update.reset();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}
          {data.sources.length > 0 ? (
            <details className="text-2xs text-muted-foreground">
              <summary className="cursor-pointer">
                Imported source history ({data.sources.length})
              </summary>
              <ul className="mt-2 space-y-1">
                {data.sources.map((source) => (
                  <li key={source.id} className="break-all">
                    {source.fileName} · {source.itemCount} items ·{' '}
                    {new Date(source.importedAt).toLocaleString()} · Submitted text SHA-256:{' '}
                    {source.textSha256}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}
    </SettingsSection>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border rounded-lg border p-2">
      <p className="text-2xs text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
